// @ts-strict-ignore
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ArticleVO } from '../../../model/vo/article.vo';
import { ArticleUsageUtils } from '../../../util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '../../../constants/article-usage-info-property-def';
import { Vector3 } from 'three';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { Busbar } from './replace-busbar-data';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArticleUtils } from '../../../util/article-utils';

export class ReplaceBusbarUtils {

  public static isBusbarArticle(article: ArticleVO): boolean {
    if (!article) {
      return false;
    }
    return ArticleUtils.isBusbarArticle(article);
  }

  public static isMoveMapContainsAllUsageIdsWithSameOffset(moveArticlesMap: Dictionary<string, Vector3>, usageIdsToCheck: string[]): boolean {
    const usageOffsets: Vector3[] = [];
    for (const usageId of usageIdsToCheck) {
      const usageOffset: Vector3 = moveArticlesMap.getValue(usageId);
      if (usageOffset) {
        usageOffsets.push(usageOffset);
      }
    }

    if (!usageOffsets.length) {
      // busbar doesn't need to be re-created if no carriers were moved
      return true;
    }
    if (usageOffsets.length !== usageIdsToCheck.length) {
      return false;
    }

    let offsetToCheck: Vector3 = null;
    for (const offset of usageOffsets) {
      if (!offsetToCheck) {
        offsetToCheck = offset;
      } else if (!MathUtils.equalsNumber3D(offsetToCheck, offset)) {
        return false;
      }
    }
    return true;
  }

  public static getCommonBusbars(leftBusbars: ArticleContainer[], rightBusbars: ArticleContainer[]): ArticleContainer[] {
    const commonBusbars: ArticleContainer[] = [];
    for (const rightBusbar of rightBusbars) {
      if (leftBusbars.indexOf(rightBusbar) >= 0) {
        commonBusbars.push(rightBusbar);
      }
    }
    return commonBusbars;
  }

  public static getBusbarStartOffset(container: ArticleContainer): number {
    if (!container) {
      return 0;
    }
    const result: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(container.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_ADDITIONAL_LENGTH_START);
    return isNaN(result) ? 0 : result;
  }

  public static getBusbarEndOffset(container: ArticleContainer): number {
    if (!container) {
      return 0;
    }
    const result: number = ArticleUsageUtils.getArticleUsageInfoPropertyFloatValue(container.articleUsage,
      ArticleUsageInfoPropertyDef.ARTICLE_ADDITIONAL_LENGTH_END);
    return isNaN(result) ? 0 : result;
  }



  public static updateMaxAllowedMoveOffsetMap(maxAllowedMoveOffsetMap: Dictionary<string, number>, movedArticles: ArticleContainer[], offset: number): void {
    if (maxAllowedMoveOffsetMap && offset > 0 && ArrayUtils.isNotEmpty(movedArticles)) {
      for (const article of movedArticles) {
        if (maxAllowedMoveOffsetMap.containsKey(article.articleGUID)) {
          const maxAllowedMoveOffset = maxAllowedMoveOffsetMap.getValue(article.articleGUID);
          maxAllowedMoveOffsetMap.setValue(article.articleGUID, maxAllowedMoveOffset - offset);
        }
      }
    }
  }

  public static getMaxAllowedMoveOffset(maxAllowedMoveOffsetMap: Dictionary<string, number>, articles: ArticleContainer[]): number {
    let minOffset: number = Number.MAX_VALUE;
    if (maxAllowedMoveOffsetMap && ArrayUtils.isNotEmpty(articles)) {
      for (const article of articles) {
        if (maxAllowedMoveOffsetMap.containsKey(article.articleGUID)) {
          const offset: number = maxAllowedMoveOffsetMap.getValue(article.articleGUID);
          if (minOffset > offset) {
            minOffset = offset;
          }
        }
      }
    }
    return minOffset;
  }

  public static getEndOverhangChildrenUsageIds(busbar: Busbar): string[] {
    if (busbar.additionalLengthEnd === 0) {
      return [];
    }
    const endOverhangStart: Vector3 = busbar.insertArticleHelper.baseLine.end.clone().add(new Vector3(busbar.additionalLengthEnd, 0, 0));
    const endOverhangLine: AdvancedLine = new AdvancedLine(endOverhangStart, busbar.insertArticleHelper.baseLine.end);
    const articles = busbar.insertArticleHelper.getConnectedArticlesFromLine(endOverhangLine);
    return articles.map((container: ArticleContainer) => container.articleGUID);
  }
}
